/**
 * ClonePortalModal — duplicate a portal with options.
 * Per portal-phases.md Phase 3.8: reset statuses, keep collaborators, keep tools, copy artifacts.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material';

interface ClonePortalModalProps {
  open: boolean;
  portalName: string;
  onClose: () => void;
  onClone: (data: {
    newName: string;
    resetStatuses: boolean;
    keepCollaborators: boolean;
    keepTools: boolean;
    copyArtifacts: boolean;
  }) => void;
}

export const ClonePortalModal: React.FC<ClonePortalModalProps> = ({
  open,
  portalName,
  onClose,
  onClone,
}) => {
  const [newName, setNewName] = useState(`Copy of ${portalName}`);
  const [resetStatuses, setResetStatuses] = useState(true);
  const [keepCollaborators, setKeepCollaborators] = useState(false);
  const [keepTools, setKeepTools] = useState(true);
  const [copyArtifacts, setCopyArtifacts] = useState(false);

  const handleClone = () => {
    if (!newName.trim()) return;
    onClone({ newName: newName.trim(), resetStatuses, keepCollaborators, keepTools, copyArtifacts });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Duplicate portal</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="New name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            size="small"
          />

          <Typography variant="subtitle2">Options</Typography>

          <FormControlLabel
            control={<Checkbox size="small" checked={resetStatuses} onChange={(e) => setResetStatuses(e.target.checked)} />}
            label='Reset all step statuses to "Not started"'
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={keepCollaborators} onChange={(e) => setKeepCollaborators(e.target.checked)} />}
            label="Keep collaborators (uncheck = fresh)"
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={keepTools} onChange={(e) => setKeepTools(e.target.checked)} />}
            label="Keep tool connections"
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={copyArtifacts} onChange={(e) => setCopyArtifacts(e.target.checked)} />}
            label="Copy artifacts"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleClone} disabled={!newName.trim()}>
          Create copy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClonePortalModal;
